import React from 'react';
import { FaSpotify } from 'react-icons/fa';
import { getLinkProvider, getLinkProviderLabel } from '../../utils/mindmap/linkProviders';
import { parseTextWithLinks } from '../../utils/common/parseTextWithLinks.js';

const urlOnlyRegex = /^https?:\/\/[^\s]+$/i;

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const safeMarkdownHref = (href) => {
  const value = String(href || '').trim();
  return /^(https?:\/\/|mailto:)/i.test(value) ? value : null;
};

const renderInlineMarkdown = (value, keyPrefix = 'inline') => {
  const text = String(value || '');
  const tokenPattern = /(`[^`\n]+`|\[[^\]\n]+\]\([^\s)]+\)|\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_)/g;
  const result = [];
  let cursor = 0;
  let match;

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > cursor) result.push(text.slice(cursor, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;

    if (token.startsWith('`')) {
      result.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = linkMatch ? safeMarkdownHref(linkMatch[2]) : null;
      result.push(href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {linkMatch[1]}
        </a>
      ) : token);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      result.push(
        <strong key={key}>{renderInlineMarkdown(token.slice(2, -2), key)}</strong>
      );
    } else {
      result.push(<em key={key}>{renderInlineMarkdown(token.slice(1, -1), key)}</em>);
    }
    cursor = match.index + token.length;
  }

  if (cursor < text.length) result.push(text.slice(cursor));
  return result;
};

const isBlockStart = (line) => /^(\s*$|```|\s{0,3}#{1,6}\s+|\s*>\s?|\s*[-*+]\s+|\s*\d+\.\s+)/.test(line);

const MarkdownPreview = ({ source }) => {
  const lines = String(source || '').split('\n');
  const blocks = [];

  for (let index = 0; index < lines.length;) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (/^\s*```/.test(line)) {
      const code = [];
      index += 1;
      while (index < lines.length && !/^\s*```/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(<pre key={`block-${index}`}><code>{code.join('\n')}</code></pre>);
      continue;
    }

    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push(React.createElement(
        `h${level}`,
        { key: `block-${index}` },
        renderInlineMarkdown(heading[2], `heading-${index}`)
      ));
      index += 1;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      const start = index;
      while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }
      blocks.push(
        <blockquote key={`block-${start}`}>
          {renderInlineMarkdown(quote.join(' '), `quote-${start}`)}
        </blockquote>
      );
      continue;
    }

    const listItem = line.match(/^\s*([-*+]|\d+\.)\s+(.+)$/);
    if (listItem) {
      const ordered = /\d+\./.test(listItem[1]);
      const items = [];
      const start = index;
      while (index < lines.length) {
        const candidate = lines[index].match(/^\s*([-*+]|\d+\.)\s+(.+)$/);
        if (!candidate || /\d+\./.test(candidate[1]) !== ordered) break;
        items.push(candidate[2]);
        index += 1;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag key={`block-${start}`}>
          {items.map((item, itemIndex) => (
            <li key={`${start}-${itemIndex}`}>
              {renderInlineMarkdown(item, `list-${start}-${itemIndex}`)}
            </li>
          ))}
        </ListTag>
      );
      continue;
    }

    const paragraph = [line.trim()];
    const start = index;
    index += 1;
    while (index < lines.length && !isBlockStart(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push(
      <p key={`block-${start}`}>
        {renderInlineMarkdown(paragraph.join(' '), `paragraph-${start}`)}
      </p>
    );
  }

  return blocks;
};

const NodeContent = ({ text, isEditing, isSelected = false, linkUrl, linkMeta }) => {
  if (isEditing) {
    return null; // Content will be handled via the textarea in Node
  }

  const rawText = typeof text === 'string' ? text : '';
  const trimmed = rawText.trim();
  const effectiveUrl = linkUrl || trimmed;
  const isUrlOnly = effectiveUrl.length > 0 && urlOnlyRegex.test(effectiveUrl);

  if (isUrlOnly) {
    try {
      const urlObj = new URL(effectiveUrl);
      const domain = urlObj.hostname.replace(/^www\./, '');
      const path = urlObj.pathname && urlObj.pathname !== '/' ? urlObj.pathname : '';
      // Prefer fetched OG title when available and the user hasn't
      // customised the text; otherwise use the node text or
      // domain/path as a fallback.
      const fallbackText = path ? `${domain}${path}` : domain;
      const metaTitleRaw = linkMeta && linkMeta.title;
      const metaTitle = metaTitleRaw ? decodeHtmlEntities(metaTitleRaw) : null;
      const displayText = metaTitle || trimmed || fallbackText;
      const MAX_TITLE_LENGTH = 27;
      const truncatedTitle =
        !isSelected && displayText && displayText.length > MAX_TITLE_LENGTH
          ? `${displayText.slice(0, MAX_TITLE_LENGTH - 1).trimEnd()}…`
          : displayText;

      // For localhost/dev URLs stored in nodes, use the current
      // window origin for favicon so they render correctly in
      // hosted environments like Netlify.
      const isLocalHost =
        urlObj.hostname === 'localhost' ||
        urlObj.hostname === '127.0.0.1';
      const faviconBase =
        typeof window !== 'undefined' && isLocalHost
          ? window.location.origin
          : urlObj.origin;
      const faviconUrl = `${faviconBase}/favicon.ico`;
      const provider = getLinkProvider(effectiveUrl);
      const isInstagram = provider === 'instagram';
      const providerLabel = getLinkProviderLabel(provider, linkMeta, domain);

      return (
        <div
          className={`node-content node-content--url ${isSelected ? 'node-content--expanded' : 'node-content--compact'}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: 'fit-content',
            maxWidth: isSelected ? 320 : 176,
            whiteSpace: 'normal',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 12,
              whiteSpace: isSelected ? 'normal' : 'nowrap',
              textOverflow: isSelected ? 'clip' : 'ellipsis',
              overflow: isSelected ? 'visible' : 'hidden',
              maxWidth: '100%',
              textAlign: 'center',
              margin: 0,
            }}
          >
            {truncatedTitle}
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              opacity: 0.85,
              justifyContent: 'center',
              maxWidth: '100%',
              marginTop: 1,
            }}
          >
            {provider === 'spotify' ? (
              <FaSpotify
                aria-hidden="true"
                style={{
                  width: 12,
                  height: 12,
                  color: '#1DB954',
                  flexShrink: 0,
                }}
              />
            ) : isInstagram ? (
              <div
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 3.5,
                  background:
                    'radial-gradient(circle at 30% 30%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 70%, #285AEB 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 2,
                    border: '1.5px solid white',
                    position: 'relative',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      width: 2,
                      height: 2,
                      borderRadius: '50%',
                      border: '1.5px solid white',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            ) : (
              faviconUrl && (
                <img
                  src={faviconUrl}
                  alt=""
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    display: 'block',
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )
            )}
            <span
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {providerLabel}
            </span>
          </div>
        </div>
      );
    } catch (e) {
      // Fall back to normal link parsing below if URL construction fails
    }
  }

  const hasMarkdown = /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s)|[*_`]/.test(rawText)
    || rawText.includes('[');
  const lengthClass = rawText.length > 180
    ? 'node-content--long'
    : rawText.length > 80
      ? 'node-content--medium'
      : 'node-content--short';
  const stateClass = isSelected ? 'node-content--expanded' : 'node-content--compact';

  if (hasMarkdown) {
    return (
      <div
        className={`node-content node-content--markdown ${lengthClass} ${stateClass}`}
        style={{ textAlign: 'center' }}
      >
        <MarkdownPreview source={rawText} />
      </div>
    );
  }

  const parts = parseTextWithLinks(rawText);

  return (
    <div
      className={`node-content node-content--plain ${lengthClass} ${stateClass}`}
      style={{ textAlign: 'center' }}
    >
      {parts.map((part, i) => {
        if (part.type === 'link') {
          return (
            <a
              key={i}
              href={part.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#add8ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                wordBreak: 'break-word',
                display: 'inline-block',
              }}
              title={part.href}
            >
              {part.content}
            </a>
          );
        }
        return <span key={i}>{part.content}</span>;
      })}
    </div>
  );
};

export default NodeContent;
